import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Component, inject, Input } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, switchMap } from 'rxjs';

import { DetachedRouterOutlet, filterBoolean } from '@core/common-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import { DataSourceActions, DataSourceSelectors, ViewListActions } from '@core/views-data';
import { dataSource$, dataSourceProvider$ } from '@core/views-util';

export const provideDataSource = () => [
  { provide: dataSourceProvider$, useValue: new BehaviorSubject(null) },
  { provide: dataSource$, useFactory: () => inject(dataSourceProvider$).pipe(filterBoolean) },
];

@Component({
  selector: 'rt-views-datasource',
  imports: [DetachedRouterOutlet],
  template: ` <rtc-detached-router-outlet /> `,
})
export class DataSourceComponent implements OnChanges, OnInit, OnDestroy {
  private readonly store = inject(Store);

  @Input() public dataSourceId: string;

  private dataSourceId$ = new BehaviorSubject<string | null>(null);
  private untilDestroyed = injectUntilDestroyed();
  private dataSourceProvider$ = inject(dataSourceProvider$);

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.dataSourceId && this.dataSourceId) {
      this.dataSourceId$.next(this.dataSourceId);
      this.store.dispatch(DataSourceActions.fetchOne({ payload: { id: this.dataSourceId } }));
      this.store.dispatch(
        ViewListActions.fetchCollection({ payload: { dataSourceId: this.dataSourceId } }),
      );
    }
  }

  public ngOnInit() {
    this.dataSourceId$
      .pipe(
        filterBoolean,
        switchMap(dataSourceId => this.store.select(DataSourceSelectors.byId(dataSourceId))),
        filterBoolean,
        this.untilDestroyed,
      )
      .subscribe(dataSource => this.dataSourceProvider$.next(dataSource));
  }

  public ngOnDestroy() {
    this.dataSourceProvider$.complete();
  }
}
